import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, Cell,
} from "recharts";
import {
  Shield, HeartHandshake, Sparkles, ArrowUpRight, ArrowRight, Quote, Users,
  Activity, TrendingUp, CalendarCheck, DoorOpen, Compass, MessageCircleHeart, Mail,
} from "lucide-react";
import { CURRICULUM } from "@/lib/curriculum";
import { LEGAL } from "@/lib/legal";
import LumaChat from "@/components/LumaChat";
import { useTrackView } from "@/lib/trackInterest";

const EMBER = "#F59E0B";

// --- Illustrative pilot snapshot (demo data — a team's real numbers appear here once EPIC begins) ---
const PULSE = Array.from({ length: 30 }, (_, i) => {
  const base = 6 + i * 0.55;
  const wave = Math.sin(i * 0.6) * 1.6;
  return { day: i + 1, checkins: Math.max(4, Math.round(base + wave)) };
});
const MOD_COMPLETION = CURRICULUM.map((m, i) => ({
  name: m.n, title: m.title, done: Math.max(5, 24 - i * 2 - (i > 4 ? 2 : 0)),
}));
const KPIS = [
  { icon: Users, value: "24", label: "Team enrolled", sub: "EPIC pilot cohort" },
  { icon: CalendarCheck, value: "137", label: "Modules completed", sub: "across the team" },
  { icon: TrendingUp, value: "+32%", label: "Avg. wellbeing lift", sub: "self-reported, 30 days" },
  { icon: Activity, value: "512", label: "Grounding check-ins", sub: "before & after shift" },
];
const STEPS = [
  { icon: DoorOpen, tag: "Day 0", title: "Enter the sanctuary", body: "The team is welcomed into a calm, private space — no pressure, no performance. Just permission to begin." },
  { icon: Compass, tag: "Days 1–30", title: "Walk the eight modules", body: "Five principles and three articles, guided by Rich. A little each day — feel it, face it, go through it." },
  { icon: Shield, tag: "Anytime", title: "Luma stands guard", body: "A 24/7 compassionate companion for the heavy moments between sessions. Private, patient, always on." },
  { icon: HeartHandshake, tag: "Day 30", title: "Share the stories", body: "The team reflects together. What broke them becomes what they can help others heal." },
];

const CardTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-black/90 border border-[#E7DFD5] px-3 py-2 text-xs font-mono text-[#1C1917]">
      <div className="text-[#78716C]">Day {label}</div>
      <div className="text-[#B45309]">{payload[0].value} check-ins</div>
    </div>
  );
};

const StatCard = ({ icon: Icon, value, label, sub }) => (
  <div className="tacc-card p-6 md:p-7" data-testid={`pilot-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <Icon size={18} className="text-[#B45309] mb-4" />
    <div className="font-display text-4xl md:text-5xl leading-none tracking-tight">{value}</div>
    <div className="text-sm text-[#1C1917] mt-3">{label}</div>
    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A8A29E] mt-1">{sub}</div>
  </div>
);

export default function EPICPilotPage() {
  const [showLuma, setShowLuma] = useState(false);
  useTrackView("pilot", "EPIC Pilot");

  useEffect(() => {
    document.title = "The EPIC Pilot · A T.A.C.C. Sanctuary";
  }, []);

  return (
    <main className="pt-28 md:pt-36 pb-28" data-testid="pilot-page">
      {/* ===== Sanctuary hero ===== */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 relative">
        <div className="absolute -top-10 right-0 w-[36rem] h-[36rem] rounded-full bg-[#F59E0B]/10 blur-[120px] pointer-events-none" />
        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
          className="relative"
        >
          {/* co-brand lockup */}
          <div className="flex items-center gap-4 mb-8" data-testid="pilot-cobrand">
            <span className="font-display text-2xl md:text-3xl tracking-tight">T.A.C.C.</span>
            <span className="text-[#A8A29E] text-xl">✕</span>
            <span className="font-mono text-sm tracking-[0.35em] uppercase text-[#B45309] border border-[#F59E0B]/40 rounded-full px-4 py-1.5">
              EPIC
            </span>
          </div>
          <div className="section-tag mb-6">§ The Sanctuary · 30-Day Pilot</div>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter max-w-4xl">
            A calm place to <span className="italic text-[#B45309]">heal</span> —
            before the shift, after the storm.
          </h1>
          <p className="text-[#78716C] text-lg leading-relaxed mt-8 max-w-2xl">
            Welcome to the EPIC pilot: thirty days where your team is given something rare — permission to
            stop, breathe, and face what they carry. Eight guided modules, a 24/7 guardian, and a sanctuary
            built on one truth: through adversity comes change.
          </p>
          <div className="flex flex-wrap gap-3 mt-10">
            <a href="#modules" className="btn-ember text-sm" data-testid="pilot-start-here">
              Start here <ArrowRight size={16} />
            </a>
            <a href="#luma" className="btn-outline text-sm" data-testid="pilot-meet-luma">
              <Shield size={16} /> Meet Luma, your Guardian
            </a>
          </div>
        </motion.div>
      </section>

      {/* ===== How the pilot works ===== */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <div className="section-tag mb-8">§ How the pilot works</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" data-testid="pilot-steps">
          {STEPS.map((s) => (
            <div key={s.title} className="tacc-card p-7 flex flex-col">
              <s.icon size={22} className="text-[#B45309]" />
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#A8A29E] mt-5">{s.tag}</div>
              <h3 className="font-display text-2xl leading-tight tracking-tight mt-1">{s.title}</h3>
              <p className="text-[#78716C] text-sm leading-relaxed mt-3">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== The eight modules ===== */}
      <section id="modules" className="max-w-[1440px] mx-auto px-6 md:px-10 mt-24 md:mt-32 scroll-mt-28">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="section-tag mb-6">§ The journey · eight modules</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight max-w-2xl">
              Five principles. Three articles. <span className="italic text-[#B45309]">One healing.</span>
            </h2>
          </div>
          <Link to="/modules/m1/experience" className="btn-ember text-sm" data-testid="pilot-begin-m1">
            Begin Module 01 <ArrowUpRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-[#E7DFD5]" data-testid="pilot-modules">
          {CURRICULUM.map((m) => (
            <Link
              key={m.id} to={`/modules/${m.id}/experience`}
              className="group border-b border-r border-[#E7DFD5] p-6 md:p-8 flex items-start gap-5 transition-colors duration-300 hover:bg-black/5"
              data-testid={`pilot-module-${m.id}`}
            >
              <span className="font-display text-4xl md:text-5xl text-[#B45309]/80 leading-none">{m.n}</span>
              <div>
                <h3 className="font-display text-2xl md:text-3xl leading-tight tracking-tight">{m.title}</h3>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716C] mt-2">{m.focus}</p>
                <p className="text-[#78716C] text-sm leading-relaxed mt-3">{m.snippet}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== Team Pulse dashboard ===== */}
      <section id="pulse" className="max-w-[1440px] mx-auto px-6 md:px-10 mt-24 md:mt-32 scroll-mt-28">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="section-tag mb-6 flex items-center gap-2"><Activity size={13} /> § Team Pulse</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight max-w-2xl">
              Watch your team <span className="italic text-[#B45309]">come back to life.</span>
            </h2>
          </div>
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#A8A29E] max-w-xs md:text-right">
            Illustrative pilot snapshot — your team's live numbers appear here once EPIC begins
          </span>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6" data-testid="pilot-kpis">
          {KPIS.map((k) => <StatCard key={k.label} {...k} />)}
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {/* daily check-ins area chart */}
          <div className="col-span-12 lg:col-span-8 tacc-card p-6 md:p-8" data-testid="pilot-pulse-chart">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl tracking-tight">Daily grounding check-ins</h3>
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A8A29E]">30 days</span>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={PULSE} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="pulseFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={EMBER} stopOpacity={0.5} />
                      <stop offset="100%" stopColor={EMBER} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="day" tick={{ fill: "#A8A29E", fontSize: 10 }} tickLine={false} axisLine={false} interval={5} />
                  <YAxis tick={{ fill: "#A8A29E", fontSize: 10 }} tickLine={false} axisLine={false} width={28} />
                  <Tooltip content={<CardTooltip />} cursor={{ stroke: "#F59E0B", strokeOpacity: 0.3 }} />
                  <Area type="monotone" dataKey="checkins" stroke={EMBER} strokeWidth={2} fill="url(#pulseFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* wellbeing ring */}
          <div className="col-span-12 lg:col-span-4 tacc-card p-6 md:p-8 flex flex-col items-center justify-center" data-testid="pilot-wellbeing-ring">
            <h3 className="font-display text-2xl tracking-tight self-start mb-6">Team wellbeing</h3>
            <div className="relative w-40 h-40">
              <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                <circle cx="60" cy="60" r="52" fill="none" stroke="#E7DFD5" strokeWidth="10" />
                <circle cx="60" cy="60" r="52" fill="none" stroke={EMBER} strokeWidth="10" strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 52} strokeDashoffset={2 * Math.PI * 52 * (1 - 0.78)} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-display text-4xl leading-none">78</span>
                <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#A8A29E] mt-1">of 100</span>
              </div>
            </div>
            <p className="text-[#78716C] text-xs text-center mt-6 leading-relaxed">
              21 of 24 active this week. Up from a baseline of 59 on day one.
            </p>
          </div>

          {/* module completion */}
          <div className="col-span-12 tacc-card p-6 md:p-8" data-testid="pilot-completion-chart">
            <h3 className="font-display text-2xl tracking-tight mb-6">Completion by module</h3>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={MOD_COMPLETION} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fill: "#A8A29E", fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fill: "#A8A29E", fontSize: 10 }} tickLine={false} axisLine={false} width={28} />
                  <Tooltip cursor={{ fill: "rgba(245,158,11,0.06)" }}
                    content={({ active, payload }) => active && payload?.length ? (
                      <div className="bg-black/90 border border-[#E7DFD5] px-3 py-2 text-xs font-mono">
                        <div className="text-[#1C1917]">{payload[0].payload.title}</div>
                        <div className="text-[#B45309]">{payload[0].value} of 24 completed</div>
                      </div>
                    ) : null} />
                  <Bar dataKey="done" radius={[3, 3, 0, 0]}>
                    {MOD_COMPLETION.map((_, i) => <Cell key={i} fill={EMBER} fillOpacity={1 - i * 0.07} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Luma Guardian ===== */}
      <section id="luma" className="max-w-[1440px] mx-auto px-6 md:px-10 mt-24 md:mt-32 scroll-mt-28">
        <div className="tacc-card p-8 md:p-14 relative overflow-hidden" data-testid="pilot-luma">
          <div className="absolute -top-20 -left-16 w-72 h-72 rounded-full bg-[#F59E0B]/10 blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-12 gap-y-8 gap-x-8 items-center">
            <div className="col-span-12 lg:col-span-7">
              <div className="section-tag mb-6 flex items-center gap-2"><Shield size={13} /> § 24/7 Guardian</div>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.92] tracking-tight">
                Nobody on your team <span className="italic text-[#B45309]">heals alone.</span>
              </h2>
              <p className="text-[#78716C] text-lg leading-relaxed mt-6 max-w-xl">
                Meet Luma — a gentle, always-on companion for the moments between sessions. Private, patient,
                no judgement. From day one, every person in the EPIC pilot has someone to talk to at 3am when
                it gets heavy. Try her yourself.
              </p>
              <button
                onClick={() => setShowLuma((v) => !v)}
                className="btn-ember text-base !px-8 !py-4 mt-9"
                data-testid="pilot-luma-toggle"
              >
                <MessageCircleHeart size={18} /> {showLuma ? "Hide Luma" : "Talk to Luma"}
              </button>
            </div>
            <div className="col-span-12 lg:col-span-5 flex justify-center">
              <div className="relative">
                <Sparkles size={120} className="text-[#B45309]/25" strokeWidth={1} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-display text-3xl italic text-[#B45309]">Luma</span>
                </div>
              </div>
            </div>
          </div>
          {showLuma && (
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="relative mt-10 pt-10 border-t border-[#E7DFD5]"
              data-testid="pilot-luma-panel"
            >
              <LumaChat />
            </motion.div>
          )}
        </div>
      </section>

      {/* ===== What EPIC leaders are saying (reserved empty state) ===== */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="section-tag mb-6">§ Reserved for you</div>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight">
            What EPIC leaders are <span className="italic text-[#B45309]">saying</span>.
          </h2>
          <p className="text-[#78716C] text-lg leading-relaxed mt-6">
            This space is left intentionally blank. In thirty days, the words of the leaders on
            <span className="text-[#1C1917]"> your </span>team land right here. Picture what they'll say.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="pilot-testimonials">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="rounded-sm border border-dashed border-[#E7DFD5] bg-black/5 p-8 flex flex-col min-h-[13rem]"
              data-testid={`pilot-testimonial-ghost-${i}`}
            >
              <Quote size={30} className="text-[#B45309]/25" />
              <div className="mt-5 space-y-3 flex-1">
                <div className="h-2.5 rounded-full bg-black/5 animate-pulse w-full" />
                <div className="h-2.5 rounded-full bg-black/5 animate-pulse w-[85%]" style={{ animationDelay: "120ms" }} />
                <div className="h-2.5 rounded-full bg-black/5 animate-pulse w-[60%]" style={{ animationDelay: "240ms" }} />
              </div>
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A8A29E] mt-6">
                — A leader on your team, 30 days from now
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Support / contact ===== */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <div className="tacc-card p-10 md:p-14 text-center" data-testid="pilot-support">
          <div className="section-tag mb-5">§ The pilot team is here</div>
          <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight max-w-2xl mx-auto">
            Questions about bringing EPIC to your team?
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-9">
            <a
              href={`mailto:${LEGAL.email}?subject=${encodeURIComponent("EPIC Pilot — let's talk")}`}
              className="btn-ember text-base !px-8 !py-4"
              data-testid="pilot-book-call"
            >
              <Mail size={18} /> Talk to us about the pilot
            </a>
            <Link to="/b2b" className="btn-outline text-sm" data-testid="pilot-b2b-link">
              See team & organization options <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
